import React, { useState, useEffect, useRef } from 'react';
import { Edit, Plus, Trash2, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Info, Download, Upload } from 'lucide-react';
import { apiService } from '../../services/apiService';
import { useAuth } from '../../context/AuthContext';
import ModelWizard from './ModelWizard';
import ModelEditModal from './ModelEditModal';
import CSVImportWizard from './CSVImportWizard';

interface AIModel {
  id: string;
  providerModelId: string;
  name: string;
  provider: string;
  description?: string;
  price_per_token?: string;
  capabilities?: Record<string, number>;
  requires_premium?: boolean;
  default_for_tasks?: string[];
  contextWindow?: number;
  maxTokens?: number;
  supports_vision?: boolean;
  supports_streaming?: boolean;
  taskDeterminationModel?: boolean;
  task_determination_priority?: number;
  category?: string;
  ranked_position?: number;
  date_of_model?: string;
  last_updated?: string;
  normalizedCost?: number;
  isUncensored?: boolean;
  isPrivacyCompliant?: boolean;

  // Custom endpoint fields
  isCustomEndpoint?: boolean;
  customEndpointUrl?: string;
  customApiKey?: string;
  customApiKeyEncrypted?: string;
  customHeaders?: Record<string, string>;
  [key: string]: any;
}


export default function Models() {
  const { user } = useAuth();
  const isAdmin = user?.isAdmin || false;
  const [models, setModels] = useState<AIModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [editingModel, setEditingModel] = useState<AIModel | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [sortField, setSortField] = useState<string>('ranked_position');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [uniqueCapabilities, setUniqueCapabilities] = useState<string[]>([]);
  const [modelTypeFilter, setModelTypeFilter] = useState<'all' | 'global' | 'custom' | 'privacy'>('all');
  const [showConfigRequirements, setShowConfigRequirements] = useState(false);
  const [importResult, setImportResult] = useState<{ imported: number; errors: { row: number; message: string }[] } | null>(null);
  const [showCSVWizard, setShowCSVWizard] = useState(false);

  const tableScrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkTableScroll = () => {
    const el = tableScrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  };

  const scrollTable = (dir: 'left' | 'right') => {
    tableScrollRef.current?.scrollBy({ left: dir === 'left' ? -320 : 320, behavior: 'smooth' });
  };

  useEffect(() => {
    loadModels();
  }, []);

  useEffect(() => {
    // Re-check after models render
    const t = setTimeout(checkTableScroll, 100);
    return () => clearTimeout(t);
  }, [models, modelTypeFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (models.length > 0) {
      const capabilitySet = new Set<string>();
      models.forEach(model => {
        if (model.capabilities) {
          Object.keys(model.capabilities).forEach(cap => capabilitySet.add(cap));
        }
      });
      setUniqueCapabilities(Array.from(capabilitySet).sort());
      
      const sorted = [...models].sort((a: AIModel, b: AIModel) => {
        let aVal: any;
        let bVal: any;
        
        if (sortField.startsWith('capability.')) {
          const capabilityName = sortField.replace('capability.', '');
          aVal = a.capabilities?.[capabilityName] || 0;
          bVal = b.capabilities?.[capabilityName] || 0;
        } else {
          aVal = a[sortField as keyof AIModel];
          bVal = b[sortField as keyof AIModel];
        }
        
        if (aVal === undefined || aVal === null) aVal = sortField === 'ranked_position' ? 999 : '';
        if (bVal === undefined || bVal === null) bVal = sortField === 'ranked_position' ? 999 : '';
        
        if (typeof aVal === 'string' && typeof bVal === 'string') {
          aVal = aVal.toLowerCase();
          bVal = bVal.toLowerCase();
        }
        
        if (sortDirection === 'asc') {
          return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
        } else {
          return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
        }
      });
      setModels(sorted);
    }
  }, [sortField, sortDirection]);

  const loadModels = async () => {
    try {
      setLoading(true);
      setError('');

      // Load both global models and user's custom models
      const [globalResponse, customResponse] = await Promise.all([
        apiService.get('/portal/models/raw'),
        apiService.get('/portal/models/custom')
      ]);

      const globalModels = Array.isArray(globalResponse.data) ? globalResponse.data :
                          Array.isArray(globalResponse) ? globalResponse : [];
      const customModels = Array.isArray(customResponse.data) ? customResponse.data :
                          Array.isArray(customResponse) ? customResponse : [];

      // Combine global and custom models
      const allModels = [...globalModels, ...customModels];

      const capabilitySet = new Set<string>();
      allModels.forEach((model: AIModel) => {
        if (model.capabilities) {
          Object.keys(model.capabilities).forEach(cap => capabilitySet.add(cap));
        }
      });
      setUniqueCapabilities(Array.from(capabilitySet).sort());

      const sortedModels = allModels.sort((a: AIModel, b: AIModel) => {
        const rankA = a.ranked_position || 999;
        const rankB = b.ranked_position || 999;
        return rankA - rankB;
      });

      setModels(sortedModels);
    } catch (err: any) {
      setError(err.message || 'Failed to load models');
      setModels([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortableHeader = ({ field, children, sticky }: { field: string; children: React.ReactNode; sticky?: 'rank' | 'name' }) => {
    const stickyClasses = sticky === 'rank'
      ? 'sticky left-0 z-10 bg-gray-50 w-20'
      : sticky === 'name'
      ? 'sticky left-[80px] z-10 bg-gray-50'
      : '';

    return (
      <th
        className={`px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none ${stickyClasses}`}
        onClick={() => handleSort(field)}
      >
        <div className="flex items-center space-x-1">
          <span>{children}</span>
          {sortField === field && (
            sortDirection === 'asc' ?
              <ChevronUp size={14} className="text-blue-600" /> :
              <ChevronDown size={14} className="text-blue-600" />
          )}
        </div>
      </th>
    );
  };

  const handleEdit = (model: AIModel) => {
    setEditingModel(model);
    setIsCreating(false);
    setIsModalOpen(true);
  };

  const handleCreate = () => {
    setEditingModel(null);
    setIsCreating(true);
    setIsModalOpen(true);
  };

  const handleDelete = async (model: AIModel) => {
    if (!window.confirm(`Delete model "${model.name}"?`)) return;

    try {
      // URL-encode the model ID to handle IDs with slashes (e.g., "venice/venice-uncensored" becomes "venice%2Fvenice-uncensored")
      await apiService.delete(`/portal/models/${encodeURIComponent(model.id)}`);
      await loadModels();
    } catch (err: any) {
      setError(err.message || 'Failed to delete model');
    }
  };

  const handleSave = async (model: AIModel): Promise<string | null> => {
    try {
      if (isCreating) {
        await apiService.post('/portal/models', model);
      } else {
        // URL-encode the model ID to handle IDs with slashes (e.g., "anthropic/claude-3-7-sonnet" becomes "anthropic%2Fclaude-3-7-sonnet")
        await apiService.put(`/portal/models/${encodeURIComponent(model.id)}`, model);
      }
      setIsModalOpen(false);
      await loadModels();
      return null; // No error
    } catch (err: any) {
      // Extract the actual error message from the API response
      const errorMessage = err.response?.data?.error?.message || err.message || 'Failed to save model';
      setError(errorMessage);

      // Show alert popup for better visibility
      alert(`Error: ${errorMessage}`);

      return errorMessage; // Return error to modal
    }
  };

  const getApiBaseUrl = () => {
    return process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api/v1' : 'http://localhost:5001/api/v1');
  };

  const handleDownloadTemplate = async () => {
    try {
      const token = localStorage.getItem('portal_token');
      const res = await fetch(`${getApiBaseUrl()}/portal/models/csv-template`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to download template');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'custom-models-template.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.message || 'Failed to download template');
    }
  };

  const handleCSVImportComplete = async (imported: number, errors: { row: number; message: string }[]) => {
    setShowCSVWizard(false);
    setImportResult({ imported, errors });
    await loadModels();
  };

  // Filter models based on model type filter
  const filteredModels = models.filter(model => {
    if (modelTypeFilter === 'all') return true;
    if (modelTypeFilter === 'global') return model.provider !== 'custom';
    if (modelTypeFilter === 'custom') return model.provider === 'custom';
    if (modelTypeFilter === 'privacy') return model.isPrivacyCompliant === true;
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Model Management</h1>
          <p className="text-sm text-gray-500 mt-1">
            Sorted by: <span className="font-medium">
              {sortField.startsWith('capability.')
                ? sortField.replace('capability.', '').charAt(0).toUpperCase() + sortField.replace('capability.', '').slice(1) + ' (Capability)'
                : sortField.replace('_', ' ')}
            </span>
            <span className="ml-1">
              {sortDirection === 'asc' ? '↑' : '↓'}
            </span>
            {' '} · Showing: <span className="font-medium">
              {filteredModels.length} of {models.length} models
            </span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleDownloadTemplate}
            className="bg-gray-100 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-200 flex items-center gap-2 text-sm"
          >
            <Download size={16} />
            Download Template
          </button>
          <button
            onClick={() => setShowCSVWizard(true)}
            className="bg-gray-100 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-200 flex items-center gap-2 text-sm"
          >
            <Upload size={16} />
            Import CSV
          </button>
          <button
            onClick={handleCreate}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center gap-2"
          >
            <Plus size={16} />
            Add Model
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      {importResult && (
        <div className="p-4 bg-green-50 border border-green-300 rounded">
          <div className="flex items-center justify-between">
            <p className="text-sm text-green-800 font-medium">
              Successfully imported {importResult.imported} model{importResult.imported !== 1 ? 's' : ''}.
              {importResult.errors.length > 0 && ` ${importResult.errors.length} row${importResult.errors.length !== 1 ? 's' : ''} had errors.`}
            </p>
            <button onClick={() => setImportResult(null)} className="text-green-600 hover:text-green-800 text-sm">Dismiss</button>
          </div>
          {importResult.errors.length > 0 && (
            <ul className="mt-2 text-xs text-red-700 list-disc list-inside">
              {importResult.errors.map((e, i) => (
                <li key={i}>Row {e.row}: {e.message}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Model Type Filter */}
      <div className="flex gap-2 items-center">
        <span className="text-sm font-medium text-gray-700">Filter by Type:</span>
        <button
          onClick={() => setModelTypeFilter('all')}
          className={`px-3 py-1.5 text-sm font-medium rounded-md ${
            modelTypeFilter === 'all'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          All Models
        </button>
        <button
          onClick={() => setModelTypeFilter('global')}
          className={`px-3 py-1.5 text-sm font-medium rounded-md ${
            modelTypeFilter === 'global'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Global Only
        </button>
        <button
          onClick={() => setModelTypeFilter('custom')}
          className={`px-3 py-1.5 text-sm font-medium rounded-md ${
            modelTypeFilter === 'custom'
              ? 'bg-purple-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Custom Only
        </button>
        <button
          onClick={() => setModelTypeFilter('privacy')}
          className={`px-3 py-1.5 text-sm font-medium rounded-md ${
            modelTypeFilter === 'privacy'
              ? 'bg-green-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Privacy-Compliant
        </button>
      </div>

      {/* Informational Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-semibold text-blue-900">Model Configuration Requirements</h3>
              <p className="text-xs text-blue-700 mt-0.5">Essential requirements for optimal system functionality</p>
            </div>
          </div>
          <button
            onClick={() => setShowConfigRequirements(!showConfigRequirements)}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-100 hover:bg-blue-200 rounded-md transition-colors"
            title={showConfigRequirements ? "Hide details" : "Show details"}
          >
            <Info className="h-4 w-4" />
            {showConfigRequirements ? 'Hide Details' : 'Show Details'}
          </button>
        </div>

        {showConfigRequirements && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 ml-8">
          {/* Required Model Types */}
          <div className="bg-white rounded-md p-4 border border-blue-100">
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Required Model Types</h4>
            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="inline-block w-2 h-2 bg-blue-500 rounded-full"></span>
                  <strong className="text-gray-900">Task Determination</strong>
                </div>
                <p className="ml-4 text-xs text-gray-600">At least one model must be marked as "Task Determination" to analyze user queries and detect requirements (privacy, vision, etc.). <span className="text-blue-600 font-medium">Recommended: Also mark privacy-compliant models as Task Determination for secure prompt analysis.</span></p>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="inline-block w-2 h-2 bg-green-500 rounded-full"></span>
                  <strong className="text-gray-900">Privacy-Compliant</strong>
                </div>
                <p className="ml-4 text-xs text-gray-600">At least one model should handle sensitive data (PII, medical records, HIPAA/GDPR). <span className="text-green-600 font-medium">Privacy-first: System always tries privacy-compliant models first for task determination.</span> <span className="text-red-600 font-medium">Requests with sensitive data will fail without one.</span></p>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="inline-block w-2 h-2 bg-purple-500 rounded-full"></span>
                  <strong className="text-gray-900">Uncensored (Optional)</strong>
                </div>
                <p className="ml-4 text-xs text-gray-600">Recommended for unrestricted content generation when explicitly requested by users</p>
              </div>
            </div>
          </div>

          {/* Important Notes */}
          <div className="bg-white rounded-md p-4 border border-blue-100">
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Important Notes</h4>
            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <strong className="text-gray-900">Model Selection Modes:</strong>
                <p className="text-xs text-gray-600 mt-1">Users can filter by <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">all</code>, <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">global_only</code>, <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">custom_only</code>, or <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">privacy_only</code>. Ensure required models exist in each mode.</p>
              </div>

              <div>
                <strong className="text-gray-900">Multi-Purpose Models:</strong>
                <p className="text-xs text-gray-600 mt-1">A single model can fulfill multiple roles (e.g., both privacy-compliant AND uncensored).</p>
              </div>

              <div>
                <strong className="text-gray-900">Fallback Behavior:</strong>
                <p className="text-xs text-gray-600 mt-1">If no models match the requirements (e.g., privacy required but no privacy-compliant models in <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">global_only</code> mode), the request will fail.</p>
              </div>
            </div>
          </div>
          </div>
        )}
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {/* Scroll hint bar */}
        <div className="flex items-center justify-end gap-1 px-4 py-2 border-b border-gray-100 bg-gray-50">
          <span className="text-xs text-gray-400 mr-1">Scroll</span>
          <button
            onClick={() => scrollTable('left')}
            disabled={!canScrollLeft}
            className="p-1 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-opacity"
            title="Scroll left"
          >
            <ChevronLeft size={16} className="text-gray-500" />
          </button>
          <button
            onClick={() => scrollTable('right')}
            disabled={!canScrollRight}
            className="p-1 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-opacity"
            title="Scroll right"
          >
            <ChevronRight size={16} className="text-gray-500" />
          </button>
        </div>
        <div ref={tableScrollRef} onScroll={checkTableScroll} className="overflow-x-auto max-h-[800px] overflow-y-auto">
          <table className="min-w-full divide-y divide-gray-200 relative">
            <thead className="bg-gray-50 sticky top-0 z-20">
              <tr>
                <SortableHeader field="ranked_position" sticky="rank">Rank</SortableHeader>
                <SortableHeader field="name" sticky="name">Name</SortableHeader>
                <SortableHeader field="provider">Provider</SortableHeader>
                <SortableHeader field="providerModelId">Model ID</SortableHeader>
                <SortableHeader field="price_per_token">Price</SortableHeader>
                <SortableHeader field="category">Category</SortableHeader>
                <SortableHeader field="contextWindow">Context Window</SortableHeader>
                <SortableHeader field="maxTokens">Max Tokens</SortableHeader>
                <SortableHeader field="supports_streaming">Streaming</SortableHeader>
                <SortableHeader field="task_determination_priority">Task Det. Priority</SortableHeader>
                <SortableHeader field="normalizedCost">Cost</SortableHeader>

                {uniqueCapabilities.map(capability => (
                  <SortableHeader key={capability} field={`capability.${capability}`}>
                    {capability.charAt(0).toUpperCase() + capability.slice(1)}
                  </SortableHeader>
                ))}
                
                <SortableHeader field="supports_vision">Vision</SortableHeader>
                <SortableHeader field="isUncensored">Uncensored</SortableHeader>
                <SortableHeader field="isPrivacyCompliant">Privacy</SortableHeader>
                <SortableHeader field="taskDeterminationModel">Task Det.</SortableHeader>
                <SortableHeader field="requires_premium">Premium</SortableHeader>
                <SortableHeader field="date_of_model">Date</SortableHeader>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 z-10 bg-gray-50 shadow-[-2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredModels.map((model) => (
                <tr key={model.id} className="group hover:bg-gray-50">
                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900 font-medium sticky left-0 z-10 bg-white group-hover:bg-gray-50 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] w-20">
                    {model.ranked_position || 'N/A'}
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-gray-900 max-w-xs truncate sticky left-[80px] z-10 bg-white group-hover:bg-gray-50 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                    {model.name}
                    {model.provider === 'custom' && (
                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                        Custom
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                    {model.provider}
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500 max-w-xs truncate">
                    {model.providerModelId}
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                    {model.price_per_token || 'N/A'}
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                    {model.category || 'N/A'}
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                    {model.contextWindow || 'N/A'}
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                    {model.maxTokens || 'N/A'}
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      model.supports_streaming ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {model.supports_streaming ? '✓' : '✗'}
                    </span>
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm text-center text-gray-900 font-medium">
                    {model.task_determination_priority || '-'}
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                    {model.normalizedCost ? `$${model.normalizedCost.toFixed(2)}` : 'Free'}
                  </td>

                  {uniqueCapabilities.map(capability => (
                    <td key={capability} className="px-3 py-4 whitespace-nowrap text-sm text-center">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold ${
                        model.capabilities?.[capability] 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-gray-100 text-gray-400'
                      }`}>
                        {model.capabilities?.[capability] || '-'}
                      </span>
                    </td>
                  ))}
                  
                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      model.supports_vision ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {model.supports_vision ? '✓' : '✗'}
                    </span>
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      model.isUncensored ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {model.isUncensored ? '✓' : '✗'}
                    </span>
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      model.isPrivacyCompliant ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {model.isPrivacyCompliant ? '✓' : '✗'}
                    </span>
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      model.taskDeterminationModel ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {model.taskDeterminationModel ? '✓' : '✗'}
                    </span>
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      model.requires_premium ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                    }`}>
                      {model.requires_premium ? 'Premium' : 'Free'}
                    </span>
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                    {model.date_of_model || 'N/A'}
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500 sticky right-0 z-10 bg-white group-hover:bg-gray-50 shadow-[-2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                    <div className="flex space-x-2">
                      {(isAdmin || model.isUserCustom) && (
                        <button
                          onClick={() => handleEdit(model)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Edit model"
                        >
                          <Edit size={16} />
                        </button>
                      )}
                      {(isAdmin || model.isUserCustom) && (
                        <button
                          onClick={() => handleDelete(model)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete model"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Use Wizard for creating new models, Edit Modal for editing existing */}
      {isCreating ? (
        <ModelWizard
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSave={handleSave}
          isAdmin={isAdmin}
          existingModels={models}
        />
      ) : (
        <ModelEditModal
          model={editingModel}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSave={handleSave}
          isAdmin={isAdmin}
          existingModels={models}
        />
      )}

      <CSVImportWizard
        isOpen={showCSVWizard}
        onClose={() => setShowCSVWizard(false)}
        onImportComplete={handleCSVImportComplete}
        existingModels={models}
        isAdmin={isAdmin}
      />
    </div>
  );
}
